<template>
	<div id="usdterc20WithdrawalForm">
		<v-form ref="form" v-model="valid" :lazy-validation="lazy">
			<form-header>
				<v-img v-bind:src="ui.logo" contain class="usdterc20-logo m-t-10 m-b-15"/>
				<template v-slot:processing_time>{{ bundleFieldValue('processing_time') }}</template>
				<template v-slot:fee>{{ bundleFieldValue('withdrawal_fee') }}</template>
				<template v-slot:min_withdrawal>{{ minimumAmountWithdrawal }} USDTERC20</template>
				<template v-slot:max_withdrawal>{{ maximumAmountWithdrawal }} USDTERC20</template>
			</form-header>
			<v-expansion-panel-content class="p-lr-10">
				<div class="withdrawal-summary-container">
					<v-row class="p-10 custom-card white-text gateway-inner-wrapper font-sm" :class="ui.classes">
						<v-col cols="12 m-b-5">
							<p>Amount to be withdrawn:</p>
						</v-col>
						<v-col cols="6">
							<p class="bold font-nm">{{ form.cryptoAmount }} USDTERC20</p>
						</v-col>
						<v-col cols="6">
							<p class="bold font-nm">{{ form.eurAmount }} EUR</p>
						</v-col>
					</v-row>

					<v-row class="m-t-15 font-sm">
						Exchange Rate: <span class="m-l-5 bold usdterc20-text" v-if="usdtErc20ExchangeRate">1 USDTERC20 = {{ adjustedUsdtErc20Value }} EUR</span>
					</v-row>
				</div>

				<div class="withdrawal-form-container">
					<v-row class="gateway-inner-wrapper m-t-15">
						<v-text-field
							v-model="form.cryptoAmount"
							:rules="formRules.cryptoAmount"
							ref="cryptoAmount"
							outlined
							label="Enter USDTERC20 Amount"
							suffix="USDTERC20"
							class="gateway-usdterc20"
							type="number"
							required
							@input="onAmountChange('cryptoAmount')"
						></v-text-field>
						<v-text-field
							v-model="form.eurAmount"
							ref="eurAmount"
							outlined
							label="Enter EUR Amount"
							suffix="EUR"
							class="gateway-usdterc20"
							type="number"
							required
							@input="onAmountChange('eurAmount')"
						></v-text-field>
						<v-text-field
							v-model="form.receive_address"
							:rules="formRules.receive_address"
							ref="receiveAddress"
							outlined
							label="Receiver Address"
							required
						></v-text-field>
						<div class="verification-wrapper">
							<div class="d-flex w-100">
								<!-- OTP -->
								<v-text-field
									v-model="form.verification_code"
									:rules="formRules.verification_code"
									ref="verificationCode"
									outlined
									:label="totpStatus === 'enabled' && !isOTPEmail ? 'Enter Google Authenticator Code' : 'Verification Code'"
									:class="isOTPEmail ? 'verification-field' : 'm-r-0'"
									type="number"
									required
								></v-text-field>
								<v-btn color="primary" v-if="isOTPEmail" class="m-black--text gradient-yellow get-code-btn" @click="requestCode()" :loading="loadingGetCode">Get Code
								</v-btn>
								<!-- End OTP -->
							</div>

							<!-- Switch -->
							<div v-if="totpStatus === 'enabled'" @click="changeOTPMethod()" class="d-block m-t-n5 m-b-1 cursor-pointer">
								<p class="font-nm bold blue-text underline">
									<span v-if="isOTPEmail">Use Google Authenticator code instead</span>
									<span v-else>Use Email OTP instead</span>
								</p>
							</div>
							<!-- End Switch -->
						</div>
					</v-row>
					<v-row class="request-btn-wrapper gateway-inner-wrapper flex-right">
						<v-btn block class="white-text gradient-green request-btn" @click="submit" :loading="loading" v-bind:disabled="!valid">Send Request
						</v-btn>
					</v-row>
				</div>
			</v-expansion-panel-content>
		</v-form>
		<get-code-success-dialog
			ref="childGetCodeSuccess"
			title="Please check your inbox"
			message="We’ve sent the OTP code to"
			:recipient="memberEmail"
			:open="dialog">
		</get-code-success-dialog>
		<error-dialog
			ref="childError"
			title="Error"
			:message="message"
			:open="errorDialog"
			toPage="">
		</error-dialog>
		<success-dialog
			ref="childSuccess"
			title="Success"
			:message="message"
			:open="successDialog"
			toPage="/transaction-history">
		</success-dialog>
	</div>
</template>

<script>
import {withdrawalFormMixin} from '../../mixins/withdrawal-form-mixin'
import {mapActions, mapGetters} from 'vuex'
import {div, inBetween, mul, toFixed} from '../../utility/number.util'

export default {
	name: "Usdterc20WithdrawalForm",
	mixins: [withdrawalFormMixin],
	data() {
		return {
			interval: null,
			form: {receiveAddress: '', cryptoAmount: '', eurAmount: '', verificationCode: ''}
		}
	},
	mounted() {
		this.interval = setInterval(() => {
			this.fetchAdjustedUsdtErc20ExchangeRates()
		}, 1000)
	},
	methods: {
		...mapActions('withdrawal', [
			'fetchAdjustedUsdtErc20ExchangeRates'
		]),
		getPayload() {
			return {
				customer_fee: this.customerFee,
				company_fee: this.companyFee,
				verification_method: this.activeOTPMethod,
				verification_code: this.form.verification_code,
				payment_option_type: this.paymentOption,
				products: [{
					username: this.customerProduct.username,
					product_code: this.customerProduct.product.code,
					amount: this.form.eurAmount,
					meta: {
						payment_details: {
							requested_crypto: this.form.cryptoAmount
						}
					}
				}],
				meta: {
					field: {
						account_id: this.form.receive_address
					},
					payment_details: {
						blockchain_rate: this.usdtErc20ExchangeRate.base_rate,
						rate: this.getAdjustedRate('eurAmount', this.form.eurAmount)
					}
				}
			}
		},
		onAmountChange(from) {
			const isFromEurAmount = from === 'eurAmount'
			const value = this.form[from] || '0.00'
			const convertedAmountField = isFromEurAmount ? 'cryptoAmount' : 'eurAmount'

			if (!isNaN(value)) {
				const adjustedRate = this.getAdjustedRate(from, value);
				this.form[convertedAmountField] = isFromEurAmount ? div(value, adjustedRate).toFixed(2) : mul(value, adjustedRate).toFixed(2)
			}
		},
		getRangeSetting(from, amount) {
			const ranges = this.usdtErc20ExchangeRate.rate.withdrawal;
			return from === 'eurAmount' ?
				ranges.filter(range => inBetween(amount, range.amountFrom, range.amountTo)).pop() :
				ranges.filter(range => inBetween(amount, range.from, range.to)).pop();
		},
		getAdjustedRate(from, amount) {
			const range = this.getRangeSetting(from, amount);
			return (range && range.adjustedRate) || this.usdtErc20ExchangeRate.base_rate;
		}
	},
	computed: {
		...mapGetters('withdrawal', [
			'usdtErc20ExchangeRate'
		]),
		minimumAmountWithdrawal() {
			return this.usdtErc20ExchangeRate ? this.usdtErc20ExchangeRate.limits.minimumAmountWithdrawal : ''
		},
		maximumAmountWithdrawal() {
			return this.usdtErc20ExchangeRate ? this.usdtErc20ExchangeRate.limits.maximumAmountWithdrawal : ''
		},
		adjustedUsdtErc20Value() {
			return toFixed(this.getAdjustedRate('cryptoAmount', 1), 8);
		}
	},
	beforeDestroy() {
		clearInterval(this.interval)
	},
}
</script>

<style scoped>
.textflash {
	-webkit-animation: flash linear 1s infinite;
	animation: flash linear 1s infinite;
}

@-webkit-keyframes flash {
	0% {
		opacity: 1;
	}
	50% {
		opacity: .1;
	}
	100% {
		opacity: 1;
	}
}

@keyframes flash {
	0% {
		opacity: 1;
	}
	50% {
		opacity: .1;
	}
	100% {
		opacity: 1;
	}
}
</style>
